import type { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IDeploymentConfigModel } from "@models/config/IDeploymentConfigModel";
import type {
  AppDevSettingKey,
  RoleModelConfig,
} from "@models/data/appSettingsDto";

/**
 * `IAppSettingsModel` — the account's SHARED application settings, cached from
 * `settings/app-settings.json` (see `models/data/appSettingsDto.ts` for the
 * document, its sparse-map rule, and the S3 key).
 *
 * **Web-only — there is no Unity counterpart.** It owns everything the AI debug
 * panel writes:
 *
 *   1. **The AI skill and role gates.** A skill switched off here is filtered out
 *      of `AIInterfaceModel.availableSkills`, which is the set every agent-facing
 *      path reads — the available-skills prompt index, the `Load_Skill`
 *      executor's lookup, and the role tool gate. So a disabled skill is not
 *      "hidden": the agent is never told it exists and cannot load it. A role
 *      switched off is filtered out of `availableRoles` the same way, which takes
 *      it out of `switch_role`'s enum and description, out of the "@" picker, and
 *      out of the persona map.
 *   2. **The core tool set.** Which tools every role keeps regardless of its
 *      `tools:` list, as a deviation from the set the build ships. It is the one
 *      tool decision that belongs to no single role, so it has nowhere else to
 *      live — see the core-tool section below.
 *   3. **The dev-panel overrides** behind {@link IDevSettingsModel}, which
 *      reads and writes them through this model rather than owning storage.
 *
 * It no longer carries the agent's prompt BODIES. Those moved to a
 * per-deployment object behind `IAIPromptStoreModel` when publishing one became
 * a replacement rather than a debug-gated shadow — they are kilobytes each, and
 * this document is republished whole on every settings write.
 *
 * **Account-scoped, not per-user.** The document sits under
 * `account/{customerId}/projectdata/`, the only tree a browser client may write
 * (its Cognito credentials are scoped there), so "global" means every member of
 * the account. It is also deliberately not per-user: these are switches for how
 * the product behaves for a customer, and a per-user copy would make two people
 * debugging the same report disagree about which skills exist.
 *
 * ## Contracts a caller depends on
 *
 * **Reads never block and never fail.** Every getter answers synchronously from
 * the cache, falling back to the stock default (`K_DEV_SETTING_DEFAULTS`;
 * skills enabled) while the document is still loading, when it has never been
 * written, and when a read failed. Readers see values change through
 * {@link onChanged} once a load lands — which is the same event a remote edit
 * arrives on, so there is only one path to handle.
 *
 * **Writes are optimistic, coalesced, and last-write-wins.** A setter applies
 * locally and emits at once, then a debounced read-modify-write publishes the
 * document. Concurrent editors are not merged field-by-field: the whole known
 * document is republished, and unknown keys (a newer build's settings) are
 * preserved. Two admins toggling different switches within the debounce window
 * on different machines will have the later write win — acceptable for an
 * admin-frequency surface, and the reason {@link modifiedBy} is recorded.
 *
 * **A failed write is reported, not swallowed.** {@link lastError} carries the
 * message for the panel's status line, and the model re-reads the document so
 * the cache converges on what the server actually holds.
 */
export interface IAppSettingsModel {
  /**
   * Fires whenever any effective value changes — a load landing, a local
   * toggle, or a failed write being rolled back by the re-read. Payload-free:
   * readers re-read what they care about, like `IDevSettingsModel.onChanged`.
   */
  readonly onChanged: Emitter<void>;

  /**
   * Whether the document has been read from the server at least once. False
   * means every getter is answering with defaults — worth surfacing in the
   * settings UI so a developer does not read "everything on" as a fact.
   */
  readonly isLoaded: boolean;

  /** A write is in flight or scheduled. Drives the panel's "Saving…" line. */
  readonly isSaving: boolean;

  /** Message from the last failed load or write, cleared by the next success. */
  readonly lastError: string | null;

  /** ISO-8601 stamp of the last write, as recorded IN the document, or null. */
  readonly modifiedAt: string | null;

  /** Display name recorded with that write, or null. */
  readonly modifiedBy: string | null;

  // ---- Skills ------------------------------------------------------

  /**
   * Whether a skill may reach the agent. True unless the document explicitly
   * disables it, so a newly shipped skill needs no settings change and an
   * unreadable document leaves every skill available.
   *
   * @param skillId the normalized id (`normalizeSkillId`), matched
   * case-insensitively.
   */
  isSkillEnabled(skillId: string): boolean;

  /** Ids the document explicitly disables. For the settings UI's summary line. */
  readonly disabledSkillIds: ReadonlyArray<string>;

  /**
   * Publishes the skill's new state. Applies locally and emits immediately;
   * resolves true once the document is written, false if the write failed (see
   * {@link lastError}). Re-enabling REMOVES the key rather than storing `true`,
   * keeping the map sparse.
   */
  setSkillEnabled(skillId: string, enabled: boolean): Promise<boolean>;

  // ---- Roles -------------------------------------------------------
  //
  // Same shape and same sparse rule as the skill gate above. What differs is the
  // POLICY: one role is the fallback the whole role system resolves to, and this
  // model deliberately has no opinion about which — `AIInterfaceModel` owns that
  // (it keeps the default role enabled whatever is stored, and moves the session
  // off a role that has just been withdrawn). Callers that care about the
  // effective answer should ask the interface model, not this.

  /** Whether the document leaves the role available. True unless explicitly disabled. */
  isRoleEnabled(roleId: string): boolean;

  /** Ids the document explicitly disables. For the settings UI's summary line. */
  readonly disabledRoleIds: ReadonlyArray<string>;

  /** Publishes the role's new state. Same optimistic contract as {@link setSkillEnabled}. */
  setRoleEnabled(roleId: string, enabled: boolean): Promise<boolean>;

  // ---- Per-role skill scope ----------------------------------------
  //
  // Which SKILLS a role can reach — the one piece of agent configuration that
  // has nowhere else to live. A skill's role scope is its `roles:` list, and for
  // skills the MANIFEST wins over the file's frontmatter while the manifest is
  // deliberately not publishable, so editing the file cannot change it. This
  // document is therefore the only runtime lever, and it is an OVERRIDE over
  // what the file says rather than the whole answer.
  //
  // Same division of labour as the role gate: this model stores a tri-state and
  // has no opinion about what it means, while `AIInterfaceModel` composes it
  // with the skill's own declaration, prunes a redundant entry back to absence,
  // and drops a loaded skill that has just left the active role's scope.

  /**
   * The stored override for one role/skill pair, or **null when there is none**
   * — which means "whatever the skill's file declares", NOT "off". Callers that
   * want the effective answer must ask `AIInterfaceModel`, which is the half
   * that can see the catalog.
   */
  getRoleSkillOverride(roleId: string, skillId: string): boolean | null;

  /**
   * Stores an override, or clears it with `null`. Same optimistic contract as
   * {@link setSkillEnabled}. Clearing the last override for a role removes the
   * role's entry entirely, keeping both levels sparse.
   */
  setRoleSkillOverride(roleId: string, skillId: string, value: boolean | null): Promise<boolean>;

  /**
   * Every stored override as `"{roleId}:{skillId}={0|1}"`, sorted — a cheap
   * value to diff when the payload-free change event fires. Readers use it to
   * tell "a scope actually moved" from "some unrelated switch did".
   */
  readonly roleSkillOverrideSignature: string;

  // ---- Core tool set -----------------------------------------------
  //
  // Which TOOLS every role keeps regardless of its `tools:` list — the
  // always-allowed set shipped as `K_CORE_ALWAYS_ALLOWED_TOOL_IDS`. Same
  // tri-state contract and same division of labour as the per-role skill scope
  // above: this model stores the deviation, `AIInterfaceModel` composes it with
  // the shipped set and prunes a redundant entry back to absence.
  //
  // It is stored here rather than edited in a role file because it is the one
  // decision that is not about any single role: the set applies to all of them,
  // and expressing "this tool is core" as six identical `tools:` edits would
  // both scale badly and lose the fact that they were one decision.

  /**
   * The stored decision for one tool, or **null when there is none** — which
   * means "whatever this build ships", NOT "not core". Callers that want the
   * effective answer must ask `AIInterfaceModel`, which is the half that knows
   * the shipped set.
   */
  getCoreToolOverride(toolId: string): boolean | null;

  /**
   * Stores a decision, or clears it with `null`. Same optimistic contract as
   * {@link setSkillEnabled}.
   */
  setCoreToolOverride(toolId: string, value: boolean | null): Promise<boolean>;

  /**
   * Every stored decision, keyed by lowercased tool id. Needed as a MAP rather
   * than only per-id because the added ids (`true`) cannot be enumerated from
   * the shipped set — they are precisely the ones that are not in it.
   */
  readonly coreToolOverrides: Readonly<Record<string, boolean>>;

  // ---- Per-role session model --------------------------------------
  //
  // Which model (and reasoning effort) the main Spark session runs while a
  // role is active — `roleModels` in the document, shipped defaults in
  // `K_ROLE_MODEL_DEFAULTS` (workflow-builder → gpt-5.6-sol / medium). Like
  // the skill/role gates and unlike the dev-panel settings, this is PRODUCT
  // configuration: account-wide, consumed headlessly by the orchestrator's
  // session policy, and NOT folded through the debug-mode gate — the session's
  // model must not change because a developer typed `potato`.

  /**
   * The role's EFFECTIVE session-model config: the stored override merged
   * field-by-field over the role's stock default. `model: null` = the stock
   * realtime model; `reasoningEffort: null` = send no `reasoning` key.
   */
  getRoleModel(roleId: string): RoleModelConfig;

  /**
   * Publishes a role's session-model config. Same optimistic contract as
   * {@link setSkillEnabled}; a config equal to the role's stock default
   * removes the entry rather than storing it, keeping the map sparse.
   *
   * A `null` field means "no stored value", which READS as the role's stock
   * default — so `null` cannot override a role that HAS a default. To put a
   * defaulted role back on the stock realtime session, pass that model
   * explicitly (`K_ROLE_MODEL_CHOICES` includes it); to send no `reasoning`
   * key on such a role, there is deliberately no spelling — `"none"` is the
   * explicit low end.
   */
  setRoleModel(roleId: string, config: RoleModelConfig): Promise<boolean>;

  // ---- Dev-panel settings ------------------------------------------

  /** The stored value, or the key's stock default when the document is silent. */
  getDevSetting(key: AppDevSettingKey): boolean;

  /**
   * Publishes a dev setting. Same optimistic contract as
   * {@link setSkillEnabled}; a value equal to the stock default removes the key
   * instead of storing it, keeping the document sparse.
   *
   * Callers go through `IDevSettingsModel`, which decides whether the setting
   * is a debug-only override or deployment-wide policy — this method has no
   * opinion about that scope.
   */
  setDevSetting(key: AppDevSettingKey, enabled: boolean): Promise<boolean>;







  // ---- Lifecycle ---------------------------------------------------

  /**
   * Supplies the cloud transport and starts the first load.
   *
   * Separate from construction because of registration ORDER: this model is
   * registered in the core IOC pass so `DevSettingsModel` (and, through it, the
   * headless workflow review model) can depend on it, while auth and storage
   * are registered later in the same function. Until this is called the model
   * is inert and serves defaults — the same state it is in before the first
   * load resolves, so there is no extra case for readers.
   *
   * `auth` is used ONLY to stamp `modifiedBy` and to obtain the token a WRITE
   * needs. The document belongs to the DEPLOYMENT, not to the account, so there
   * are no sign-in / sign-out subscriptions: a different account does not have
   * different settings, and clearing them on sign-out would have withdrawn every
   * skill and role from the login screen's own agent.
   */
  attachTransport(auth: IAuthModel, config: IDeploymentConfigModel): void;

  /** Loads once, and joins an in-flight load. No-op with no transport or user. */
  ensureLoadedAsync(): Promise<void>;

  /** Forces a re-read, e.g. from the settings panel's Refresh action. */
  reloadAsync(): Promise<void>;
}

export const APP_SETTINGS_MODEL = Symbol.for("Campfire.AppSettingsModel");
